import React, { useEffect, useState } from "react";
import { backendApi } from "@/api/backendApi";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock } from "lucide-react";
import { Link } from "react-router-dom";

export default function Interviews() {
  const [interviews, setInterviews] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [jobs, setJobs] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      const intv = await backendApi.entities.Interview.list().catch(() => []);
      const cands = await backendApi.entities.Candidate.list().catch(() => []);
      const jobs = await backendApi.entities.Job.list().catch(() => []);

      setInterviews(intv);
      setCandidates(cands);
      setJobs(jobs);
    } catch (error) {
      console.error("Failed to load interview data:", error);
    }
  }

  const getCandidate = (id) => candidates.find((c) => c.id === id);
  const getJob = (id) => jobs.find((j) => j.id === id);

  const today = new Date().toDateString();

  const todayInterviews = interviews.filter(
    (i) => new Date(i.date).toDateString() === today
  );
  const weekInterviews = interviews.filter(
    (i) => new Date(i.date).toDateString() !== today
  );

  return (
    <div className="space-y-10">

      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Interviews</h1>
        <p className="text-slate-500 mt-1">
          Schedule and manage candidate interviews
        </p>
      </div>

      {/* Schedule Button */}
      <div className="flex justify-end">
        <Link
          to="/recruit/schedule-interview"
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-2 shadow"
        >
          + Schedule Interview
        </Link>
      </div>

      {/* Today Section */}
      <Section title="Today" list={todayInterviews} getCandidate={getCandidate} getJob={getJob} />

      {/* This Week Section */}
      <Section title="This Week" list={weekInterviews} getCandidate={getCandidate} getJob={getJob} />

    </div>
  );
}

/* ----------------------------
   SECTION COMPONENT
----------------------------- */
function Section({ title, list, getCandidate, getJob }) {
  if (!list || list.length === 0) return null;

  return (
    <div className="space-y-4">
      <h2 className="font-semibold text-lg text-slate-800">{title}</h2>

      {list.map((intv) => {
        const candidate = getCandidate(intv.candidate_id);
        const job = getJob(intv.job_id);

        if (!candidate) return null;

        const initials = candidate.full_name
          ?.split(" ")
          .map((n) => n[0])
          .join("")
          .toUpperCase();

        return (
          <div
            key={intv.id}
            className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 flex justify-between items-center"
          >
            {/* Left Content */}
            <div className="flex items-center gap-4">

              {/* Avatar */}
              <Avatar className="h-12 w-12 border">
                {candidate.photo_url ? (
                  <AvatarImage src={candidate.photo_url} />
                ) : (
                  <AvatarFallback className="bg-indigo-600 text-white font-medium">
                    {initials}
                  </AvatarFallback>
                )}
              </Avatar>

              {/* Text */}
              <div>
                <p className="font-medium text-slate-900">{candidate.full_name}</p>
                <p className="text-sm text-slate-500">{job?.title}</p>

                <div className="flex items-center gap-4 mt-2 text-sm text-slate-600">

                  {/* Date */}
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4 text-slate-500" />
                    {new Date(intv.date).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </div>

                  {/* Time */}
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4 text-slate-500" />
                    {intv.time}
                  </div>

                  {/* Type Badge */}
                  <Badge className="bg-slate-100 text-slate-700">
                    {intv.type}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Status Badge */}
            <Badge className="bg-blue-50 text-blue-600 border-blue-200 px-3 py-1 text-xs">
              scheduled
            </Badge>
          </div>
        );
      })}
    </div>
  );
}
